import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { KeyValueChildrenModel } from '@models/common/keyValueModel';
import { ReorderRelationsService } from '@module-definition/reorder-relations/common';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { ConfirmationDialog } from '@shared/dialogs';
import { CipoDialogModule } from '@shared/modules/cipo-dialog.module';
import { UtilsService } from '@shared/services';
import { NotificationService } from '@shared/services';
import { filter, finalize } from 'rxjs';

@Component({
  selector: 'cipo-reorder-relations',
  imports: [
    MatIconModule,
    MatButtonModule,
    CipoDialogModule,
    MatExpansionModule,
    FlexLayoutModule,
    CommonModule,
    DragDropModule,
  ],
  templateUrl: './reorder-relations.component.html',
  // needed here to override the disabled styles of mat expansion panel header, need to disable mat-expansion-panel so it can't be expanded
  styles: `
    .mat-expansion-panel-header[aria-disabled='true'] .mat-expansion-panel-header-title,
    .mat-expansion-panel-header[aria-disabled='true'] .mat-expansion-panel-header-description {
      color: var(--black-900);
    }
  `,
})
export class ReorderRelationsComponent implements OnInit {
  dialog = inject(MatDialog);
  notification = inject(NotificationService);
  utils = inject(UtilsService);
  reorderRelationsService = inject(ReorderRelationsService);

  skeletonItems = Array(5);
  initialRelations: KeyValueChildrenModel<number, string>[] = [];
  relations = signal<KeyValueChildrenModel<number, string>[]>([]);
  onClose = output<boolean>({ alias: 'onclose' });
  anythingChanged = signal(false);
  isLoading = signal(false);

  ngOnInit(): void {
    this.isLoading.set(true);
    this.reorderRelationsService
      .get()
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe(relations => {
        this.relations.set(relations);
        this.initialRelations = structuredClone(relations);
      });
  }

  onCloseClick() {
    if (this.anythingChanged()) {
      this.dialog
        .open(ConfirmationDialog, {
          data: {
            title: 'pp.sectionsGoBackErrorTitle',
            description: 'pp.sectionsGoBackErrorMessage',
          },
        })
        .afterClosed()
        .pipe(filter(val => !!val))
        .subscribe(() => {
          this.onClose.emit(true);
        });
    } else {
      this.onClose.emit(true);
    }
  }

  drop(event: CdkDragDrop<KeyValueChildrenModel<number, string>[]>) {
    this.utils.moveItemsInSignalArray(this.relations, event.previousIndex, event.currentIndex);
    this.relations.update(list => list.map((item, index) => ({ ...item, order: index })));

    this.anythingChanged.set(true);
  }

  dropChild(relationId: number, event: CdkDragDrop<KeyValueChildrenModel<number, string>[]>) {
    const relation = this.relations().find(r => r.key === relationId);
    if (relation && relation.children) {
      moveItemInArray(relation.children, event.previousIndex, event.currentIndex);
      relation.children = relation.children.map((item, index) => ({ ...item, order: index }));
      this.relations.update(list => list.map(item => (item.key === relationId ? relation : item)));
      this.anythingChanged.set(true);
    }
  }

  discardChanges() {
    this.relations.set(structuredClone(this.initialRelations));
    this.anythingChanged.set(false);
  }

  save() {
    this.isLoading.set(true);
    this.reorderRelationsService
      .save(this.relations())
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe(() => {
        this.notification.success('pp.reorderRelationsSaved');
        this.initialRelations = structuredClone(this.relations());
        this.anythingChanged.set(false);
      });
  }
}
